<template>
  <div class="odoo-page space-y-5">
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <nav class="flex items-center text-sm text-neutral-500 dark:text-neutral-400 mb-1" aria-label="breadcrumb">
          <router-link :to="{ name: 'Suppliers' }" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Suppliers</router-link>
          <span class="material-icons text-sm mx-1" aria-hidden="true">chevron_right</span>
          <span class="text-neutral-900 dark:text-neutral-100 font-medium">Odoo Sync</span>
        </nav>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Odoo Supplier Sync</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Manage and sync vendor contacts from Odoo</p>
      </div>
      <div class="flex items-center gap-2.5">
        <!-- Connection Status (click to re-check) -->
        <button
          type="button"
          @click="recheckConnection"
          :disabled="checkingHealth"
          title="Re-check Odoo connection"
          aria-label="Re-check Odoo connection"
          class="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-wait"
          :class="connectionClass">
          <span class="w-2 h-2 rounded-full" :class="dotClass" aria-hidden="true"></span>
          {{ connectionLabel }}
        </button>
        <!-- Sync Button -->
        <button
          @click="handleSync"
          :disabled="syncing || !odooConnected"
          :title="!odooConnected ? 'Odoo is disconnected — re-check the connection first' : null"
          class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span class="material-icons text-[18px]" :class="syncing ? 'animate-spin' : ''" aria-hidden="true">sync</span>
          {{ syncing ? 'Syncing...' : 'Sync Now' }}
        </button>
      </div>
    </div>

    <!-- Sync Result Banner -->
    <AlertBanner
      v-if="syncResult"
      :type="syncResult.status === 'Complete' ? 'success' : 'error'"
      :message="syncResult.status === 'Complete' ? `Sync complete — ${syncResult.syncedCount} contacts synced` : `Sync failed: ${syncResult.error}`"
      @dismiss="syncResult = null"
    />

    <!-- Filters Row -->
    <div class="flex flex-wrap items-center gap-3">
      <!-- Tab Pills -->
      <div class="flex gap-1 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
        <button
          @click="activeTab = 'all'"
          :aria-pressed="activeTab === 'all'"
          class="px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200"
          :class="activeTab === 'all' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'"
        >
          All
          <span class="ml-1 text-xs tabular-nums opacity-60">{{ filteredContacts.length }}</span>
        </button>
        <button
          @click="activeTab = 'unmatched'"
          :aria-pressed="activeTab === 'unmatched'"
          class="px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200"
          :class="activeTab === 'unmatched' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'"
        >
          Unmatched
          <span class="ml-1 text-xs tabular-nums text-amber-600 dark:text-amber-400">{{ unmatchedCount }}</span>
        </button>
        <button
          @click="activeTab = 'linked'"
          :aria-pressed="activeTab === 'linked'"
          class="px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200"
          :class="activeTab === 'linked' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'"
        >
          Linked
          <span class="ml-1 text-xs tabular-nums text-emerald-600 dark:text-emerald-400">{{ linkedCount }}</span>
        </button>
      </div>

      <!-- Company Filter -->
      <select
        v-model="selectedCompany"
        aria-label="Filter by company"
        class="px-3 py-1.5 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-neutral-50 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 cursor-pointer"
      >
        <option :value="null">All Companies</option>
        <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>
    </div>

    <!-- Fetch error with nothing loaded — never show the 'Sync Now' empty state for a failure -->
    <ErrorState
      v-if="error && !contacts.length"
      title="Couldn't load Odoo contacts"
      :message="error"
      @retry="retryFetchContacts"
    />

    <!-- Contacts Table -->
    <template v-else>
      <AlertBanner
        v-if="error"
        type="error"
        message="Couldn't refresh Odoo contacts — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />
      <DataTable
      :data="displayedContacts"
      :columns="tableColumns"
      :loading="loading"
      :searchable="true"
      :show-index="true"
      :pagination="true"
      :clickable="true"
      v-model:page="tablePage"
      row-key="id"
      loading-text="Loading Odoo contacts..."
      empty-text="No contacts found. Click 'Sync Now' to pull contacts from Odoo."
      @row-click="openContactDetail"
    >
      <template #cell-name="{ row }">
        <button
          @click.stop="openContactDetail(row)"
          :aria-label="`View details for ${row.name}`"
          class="text-left text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
        >
          {{ row.name }}
        </button>
      </template>

      <template #cell-companyName="{ row }">
        <span class="text-xs">{{ row.companyName || '—' }}</span>
      </template>

      <template #cell-status="{ row }">
        <span v-if="row.linkedSupplierId"
          class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
          <span class="w-1.5 h-1.5 rounded-full bg-green-500"></span>
          Linked
        </span>
        <span v-else
          class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
          <span class="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
          Unmatched
        </span>
      </template>

      <template #cell-syncedAt="{ row }">
        <span class="text-xs text-neutral-500 dark:text-neutral-400">
          {{ formatDate(row.syncedAt) }}
        </span>
      </template>

      <template #actions="{ row }">
        <div class="flex items-center justify-center gap-1">
          <button v-if="!row.linkedSupplierId"
            @click.stop="openLinkModal(row)"
            class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg text-neutral-400 hover:text-secondary-600 hover:bg-secondary-50 dark:hover:text-secondary-400 dark:hover:bg-secondary-900/30 transition-all duration-150"
            title="Link to existing supplier"
            aria-label="Link to existing supplier">
            <span class="material-icons text-[16px]" aria-hidden="true">link</span>
          </button>
          <button v-if="!row.linkedSupplierId"
            @click.stop="openCreateModal(row)"
            class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg text-neutral-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-emerald-400 dark:hover:bg-emerald-900/30 transition-all duration-150"
            title="Create new supplier"
            aria-label="Create new supplier">
            <span class="material-icons text-[16px]" aria-hidden="true">add_circle_outline</span>
          </button>
          <button v-if="row.linkedSupplierId"
            @click.stop="openUnlinkConfirm(row)"
            class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-all duration-150"
            title="Unlink supplier"
            aria-label="Unlink supplier">
            <span class="material-icons text-[16px]" aria-hidden="true">link_off</span>
          </button>
          <router-link v-if="row.linkedSupplierId"
            :to="{ name: 'SupplierView', params: { id: row.linkedSupplierId } }"
            @click.stop
            class="inline-flex items-center justify-center min-w-[40px] min-h-[40px] rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 transition-all duration-150"
            title="View supplier"
            aria-label="View supplier">
            <span class="material-icons text-[16px]" aria-hidden="true">open_in_new</span>
          </router-link>
        </div>
      </template>
      </DataTable>
    </template>

    <!-- Contact Detail Modal -->
    <Modal :show="showDetailModal" title="Odoo Contact Details" size="lg" @close="showDetailModal = false">
      <div v-if="selectedContact" class="space-y-6">
        <!-- Contact Info Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-3">
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Odoo Name</label>
              <p class="text-sm text-neutral-900 dark:text-neutral-100 font-medium mt-0.5">{{ selectedContact.name }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Odoo ID</label>
              <p class="text-sm text-neutral-900 dark:text-neutral-100 mt-0.5">{{ selectedContact.odooId }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Company</label>
              <p class="text-sm text-neutral-900 dark:text-neutral-100 mt-0.5">{{ selectedContact.companyName || '—' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">VAT / Tax ID</label>
              <p class="text-sm text-neutral-900 dark:text-neutral-100 mt-0.5">{{ selectedContact.vat || '—' }}</p>
            </div>
          </div>
          <div class="space-y-3">
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Address</label>
              <p class="text-sm text-neutral-900 dark:text-neutral-100 mt-0.5">
                {{ [selectedContact.street, selectedContact.street2, selectedContact.city, selectedContact.countryName].filter(Boolean).join(', ') || '—' }}
              </p>
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Email</label>
              <p class="text-sm text-primary-600 dark:text-primary-400 mt-0.5">{{ selectedContact.email || '—' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Phone</label>
              <p class="text-sm text-neutral-900 dark:text-neutral-100 mt-0.5">{{ selectedContact.phone || '—' }}</p>
            </div>
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Coordinates</label>
              <p class="text-sm text-neutral-900 dark:text-neutral-100 mt-0.5">
                {{ selectedContact.lat && selectedContact.lng ? `${selectedContact.lat}, ${selectedContact.lng}` : 'Not available' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Status Section -->
        <div class="border-t border-neutral-200 dark:border-neutral-700 pt-4">
          <div class="flex items-center justify-between">
            <div>
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Link Status</label>
              <div class="mt-1">
                <span v-if="selectedContact.linkedSupplierId"
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                  <span class="w-2 h-2 rounded-full bg-green-500"></span>
                  Linked to supplier: {{ selectedContact.linkedSupplierId }}
                </span>
                <span v-else
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                  <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
                  Not linked — needs matching
                </span>
              </div>
            </div>
            <div class="text-right">
              <label class="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Last Synced</label>
              <p class="text-sm text-neutral-900 dark:text-neutral-100 mt-0.5">{{ formatDate(selectedContact.syncedAt) }}</p>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button v-if="selectedContact && !selectedContact.linkedSupplierId"
            @click="showDetailModal = false; openLinkModal(selectedContact)"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm">
            Link to Supplier
          </button>
          <button v-if="selectedContact && !selectedContact.linkedSupplierId"
            @click="showDetailModal = false; openCreateModal(selectedContact)"
            class="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors font-medium text-sm">
            Create Supplier
          </button>
          <button v-if="selectedContact && selectedContact.linkedSupplierId"
            @click="showDetailModal = false; openUnlinkConfirm(selectedContact)"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm">
            Unlink Supplier
          </button>
          <router-link v-if="selectedContact && selectedContact.linkedSupplierId"
            :to="{ name: 'SupplierView', params: { id: selectedContact.linkedSupplierId } }"
            class="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors font-medium text-sm">
            View Supplier
          </router-link>
          <button @click="showDetailModal = false"
            class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors font-medium text-sm">
            Close
          </button>
        </div>
      </template>
    </Modal>

    <!-- Link Supplier Modal -->
    <Modal :show="showLinkModal" title="Link to Existing Supplier" size="lg" @close="closeLinkModal">
      <div v-if="linkContact" class="space-y-5">
        <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p class="text-sm text-blue-800 dark:text-blue-300">
            <span class="font-semibold">Odoo Contact:</span> {{ linkContact.name }}
            <span class="text-primary-600 dark:text-primary-400 ml-2">(ID: {{ linkContact.odooId }})</span>
          </p>
        </div>

        <!-- Supplier Search -->
        <div>
          <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Search Existing Suppliers</label>
          <input
            v-model="supplierSearch"
            type="text"
            placeholder="Type to search suppliers..."
            class="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          />
        </div>

        <!-- Supplier List -->
        <div class="max-h-64 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg">
          <div v-if="!filteredSuppliers.length" class="py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
            {{ supplierSearch ? 'No suppliers match your search' : (suppliersLoaded ? 'No suppliers found for this site' : 'Loading suppliers...') }}
          </div>
          <div v-for="supplier in filteredSuppliers" :key="supplier.id"
            @click="!isSupplierFullyLinked(supplier) && selectSupplierForLink(supplier)"
            :class="[
              'flex items-center justify-between px-4 py-3 transition-colors border-b border-neutral-100 dark:border-neutral-700 last:border-b-0',
              isSupplierFullyLinked(supplier)
                ? 'opacity-60 cursor-not-allowed'
                : selectedSupplierForLink?.id === supplier.id
                  ? 'cursor-pointer bg-primary-50 dark:bg-primary-900/20 border-l-4 border-l-primary-600'
                  : 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700'
            ]">
            <div>
              <p class="text-sm font-medium text-neutral-900 dark:text-neutral-100">{{ supplier.suppliername }}</p>
              <p class="text-xs text-neutral-500 dark:text-neutral-400">{{ supplier.geotag || 'No address' }}</p>
              <p v-if="supplier.locations && supplier.locations.length" class="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                {{ supplier.locations.length }} location(s)
              </p>
            </div>
            <div v-if="isSupplierFullyLinked(supplier)" class="text-xs text-yellow-600 dark:text-yellow-400 font-medium" title="This supplier is already linked to another Odoo contact">
              Already linked
            </div>
            <span v-else-if="selectedSupplierForLink?.id === supplier.id" class="material-icons text-primary-600 dark:text-primary-400" aria-hidden="true">check_circle</span>
          </div>
        </div>

        <!-- Location Picker (shown when selected supplier has locations) -->
        <div v-if="selectedSupplierForLink && selectedSupplierForLink.locations && selectedSupplierForLink.locations.length">
          <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Select Location</label>
          <div class="max-h-40 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg">
            <div v-for="loc in selectedSupplierForLink.locations" :key="loc.locationid"
              @click="!loc.odooId && (selectedLocationForLink = loc)"
              :class="[
                'flex items-center justify-between px-4 py-2.5 transition-colors border-b border-neutral-100 dark:border-neutral-700 last:border-b-0',
                loc.odooId
                  ? 'opacity-60 cursor-not-allowed'
                  : selectedLocationForLink?.locationid === loc.locationid
                    ? 'cursor-pointer bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-600'
                    : 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700'
              ]">
              <div>
                <p class="text-sm font-medium text-neutral-900 dark:text-neutral-100">{{ loc.locationname || 'Location ' + loc.locationid }}</p>
                <p v-if="loc.areaname && loc.areaname !== 'NIL'" class="text-xs text-neutral-500 dark:text-neutral-400">{{ loc.areaname }}</p>
              </div>
              <div v-if="loc.odooId" class="text-xs text-yellow-600 dark:text-yellow-400 font-medium" title="This location is already linked to another Odoo contact">
                Already linked
              </div>
              <span v-else-if="selectedLocationForLink?.locationid === loc.locationid" class="material-icons text-primary-600 dark:text-primary-400" aria-hidden="true">check_circle</span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button @click="closeLinkModal"
            class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors font-medium text-sm">
            Cancel
          </button>
          <button
            @click="handleLink"
            :disabled="!canLink || linkingInProgress"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {{ linkingInProgress ? 'Linking...' : (selectedLocationForLink ? 'Link to Location' : 'Link Supplier') }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Create Supplier Modal -->
    <Modal :show="showCreateModal" title="Create New Supplier" size="lg" @close="closeCreateModal">
      <div v-if="createContact" class="space-y-5">
        <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p class="text-sm text-green-800 dark:text-green-300">
            <span class="font-semibold">Odoo Contact:</span> {{ createContact.name }}
            <span class="text-green-600 dark:text-green-400 ml-2">(ID: {{ createContact.odooId }})</span>
          </p>
        </div>

        <form @submit.prevent="handleCreate" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Operational Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="newSupplierName"
              type="text"
              required
              placeholder="Display name for OMS"
              class="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
            />
            <p class="text-xs text-neutral-500 dark:text-neutral-400 mt-1">This is the name that will appear in OMS. It may differ from the Odoo legal name.</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Site <span class="text-red-500">*</span>
            </label>
            <select
              v-model="newSupplierSiteId"
              required
              class="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
            >
              <option value="" disabled>Select a site</option>
              <option v-for="site in userSites" :key="site.siteId" :value="site.siteId">{{ site.siteName }}</option>
            </select>
          </div>

          <!-- Contact Preview -->
          <div class="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
            <h4 class="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">Data from Odoo</h4>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span class="text-neutral-500 dark:text-neutral-400">Address:</span>
                <span class="text-neutral-900 dark:text-neutral-100 ml-1">{{ createContact.street || '—' }}</span>
              </div>
              <div>
                <span class="text-neutral-500 dark:text-neutral-400">City:</span>
                <span class="text-neutral-900 dark:text-neutral-100 ml-1">{{ createContact.city || '—' }}</span>
              </div>
              <div>
                <span class="text-neutral-500 dark:text-neutral-400">Email:</span>
                <span class="text-neutral-900 dark:text-neutral-100 ml-1">{{ createContact.email || '—' }}</span>
              </div>
              <div>
                <span class="text-neutral-500 dark:text-neutral-400">Phone:</span>
                <span class="text-neutral-900 dark:text-neutral-100 ml-1">{{ createContact.phone || '—' }}</span>
              </div>
              <div>
                <span class="text-neutral-500 dark:text-neutral-400">Coordinates:</span>
                <span class="text-neutral-900 dark:text-neutral-100 ml-1">
                  {{ createContact.lat && createContact.lng ? `${createContact.lat}, ${createContact.lng}` : 'None' }}
                </span>
              </div>
              <div>
                <span class="text-neutral-500 dark:text-neutral-400">Country:</span>
                <span class="text-neutral-900 dark:text-neutral-100 ml-1">{{ createContact.countryName || '—' }}</span>
              </div>
            </div>
          </div>
        </form>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button @click="closeCreateModal"
            class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors font-medium text-sm">
            Cancel
          </button>
          <button
            @click="handleCreate"
            :disabled="!newSupplierName || !newSupplierSiteId || creatingInProgress"
            class="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {{ creatingInProgress ? 'Creating...' : 'Create Supplier' }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Unlink Confirm Modal -->
    <Modal :show="showUnlinkModal" title="Unlink Supplier" size="sm" @close="showUnlinkModal = false">
      <div v-if="unlinkContact" class="space-y-4">
        <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p class="text-sm text-red-800 dark:text-red-300">
            Are you sure you want to unlink <span class="font-semibold">{{ unlinkContact.name }}</span> from its supplier?
          </p>
          <p class="text-xs text-red-600 dark:text-red-400 mt-2">
            This will remove the cross-reference between the Odoo contact and the Firebase supplier. The supplier record will not be deleted.
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button @click="showUnlinkModal = false"
            class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors font-medium text-sm">
            Cancel
          </button>
          <button
            @click="handleUnlink"
            :disabled="unlinkingInProgress"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {{ unlinkingInProgress ? 'Unlinking...' : 'Confirm Unlink' }}
          </button>
        </div>
      </template>
    </Modal>

  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { collection, onSnapshot, orderBy, query } from '@firebase/firestore'
import { db } from '@/firebase/config'
import { useOdooApi } from '@/composables/useOdooApi'
import useToast from '@/composables/useToast'
import DataTable from '@/components/ui/DataTable.vue'
import Modal from '@/components/ui/Modal.vue'
import ErrorState from '@/components/ui/ErrorState.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'

export default {
  components: { DataTable, Modal, ErrorState, AlertBanner },
  setup() {
    const store = useStore()
    store.commit('setCurrentRoute', 'OdooContacts')
    const { showToast } = useToast()

    const {
      loading, error, healthStatus, companies, contacts,
      checkHealth, fetchCompanies, syncContacts, fetchContacts,
      linkSupplier, unlinkSupplier, createSupplier
    } = useOdooApi()

    // State
    const activeTab = ref('all')
    // Tabs filter the table from outside it — jump back to page 1 on switch.
    const tablePage = ref(1)
    watch(activeTab, () => { tablePage.value = 1 })
    const selectedCompany = ref(null)
    const syncing = ref(false)
    const syncResult = ref(null)

    // Detail modal
    const showDetailModal = ref(false)
    const selectedContact = ref(null)

    // Link modal
    const showLinkModal = ref(false)
    const linkContact = ref(null)
    const supplierSearch = ref('')
    const selectedSupplierForLink = ref(null)
    const selectedLocationForLink = ref(null)
    const linkingInProgress = ref(false)
    const allSuppliers = ref([])
    const suppliersLoaded = ref(false)

    // Create modal
    const showCreateModal = ref(false)
    const createContact = ref(null)
    const newSupplierName = ref('')
    const newSupplierSiteId = ref('')
    const creatingInProgress = ref(false)

    // Unlink modal
    const showUnlinkModal = ref(false)
    const unlinkContact = ref(null)
    const unlinkingInProgress = ref(false)

    // User sites from store
    const userSites = computed(() => store.state.userSites || [])

    // Connection status
    const odooConnected = computed(() => healthStatus.value?.status === 'connected')
    const checkingHealth = ref(false)
    const recheckConnection = async () => {
      checkingHealth.value = true
      healthStatus.value = null // shows 'Checking...' with the pulse dot
      try {
        await checkHealth()
      } finally {
        checkingHealth.value = false
      }
    }
    const connectionClass = computed(() => {
      if (!healthStatus.value) return 'border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800'
      if (healthStatus.value.status === 'connected') return 'border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
      return 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
    })
    const dotClass = computed(() => {
      if (!healthStatus.value) return 'bg-neutral-400 animate-pulse'
      if (healthStatus.value.status === 'connected') return 'bg-green-500'
      return 'bg-red-500'
    })
    const connectionLabel = computed(() => {
      if (!healthStatus.value) return 'Checking...'
      if (healthStatus.value.status === 'connected') return 'Odoo Connected'
      return 'Disconnected'
    })

    // Table columns
    const tableColumns = [
      { key: 'name', label: 'Name', sortable: true, align: 'left' },
      { key: 'companyName', label: 'Company', sortable: true, align: 'center' },
      { key: 'city', label: 'City', sortable: true, align: 'center' },
      { key: 'phone', label: 'Phone', sortable: true, align: 'center' },
      { key: 'email', label: 'Email', sortable: true, align: 'center' },
      { key: 'status', label: 'Status', sortable: false, align: 'center' },
      { key: 'syncedAt', label: 'Last Synced', sortable: true, align: 'center' },
    ]

    // Filtered contacts by company
    const filteredContacts = computed(() => {
      let list = contacts.value || []
      if (selectedCompany.value) {
        list = list.filter(c => c.companyId === selectedCompany.value)
      }
      return list
    })

    const unmatchedCount = computed(() => filteredContacts.value.filter(c => !c.linkedSupplierId).length)
    const linkedCount = computed(() => filteredContacts.value.filter(c => c.linkedSupplierId).length)

    // Displayed contacts based on active tab
    const displayedContacts = computed(() => {
      if (activeTab.value === 'unmatched') return filteredContacts.value.filter(c => !c.linkedSupplierId)
      if (activeTab.value === 'linked') return filteredContacts.value.filter(c => c.linkedSupplierId)
      return filteredContacts.value
    })

    // Filtered suppliers for link modal
    const filteredSuppliers = computed(() => {
      if (!supplierSearch.value) return allSuppliers.value
      const q = supplierSearch.value.toLowerCase()
      return allSuppliers.value.filter(s =>
        s.suppliername?.toLowerCase().includes(q) ||
        s.geotag?.toLowerCase().includes(q) ||
        s.contactemailaddress?.toLowerCase().includes(q)
      )
    })

    // A supplier with no locations and an odooId can't take another link.
    const isSupplierFullyLinked = (supplier) =>
      !!supplier.odooId && !(supplier.locations && supplier.locations.length)

    // Load suppliers from Firebase for linking
    let unsubSuppliers = null
    const loadSuppliers = () => {
      let colRef = collection(db, 'suppliers')
      colRef = query(colRef, orderBy('suppliername'))
      unsubSuppliers = onSnapshot(colRef, (snapshot) => {
        let results = []
        snapshot.docs.forEach((doc) => {
          if (doc.data().siteid === store.state.currentSite) {
            results.push({ ...doc.data(), id: doc.id })
          }
        })
        allSuppliers.value = results
        suppliersLoaded.value = true
      }, () => {
        // Mark resolved so the link modal doesn't claim 'Loading...' forever.
        suppliersLoaded.value = true
      })
    }

    onUnmounted(() => {
      if (unsubSuppliers) unsubSuppliers()
    })

    // Format date helper
    const formatDate = (dateStr) => {
      if (!dateStr) return '—'
      try {
        return new Date(dateStr).toLocaleString()
      } catch {
        return dateStr
      }
    }

    // Clear the composable's sticky error before refetching so a successful
    // retry dismisses the ErrorState/banner.
    const retryFetchContacts = async () => {
      error.value = null
      await fetchContacts()
    }

    // Actions
    const handleSync = async () => {
      syncing.value = true
      syncResult.value = null
      try {
        const result = await syncContacts(selectedCompany.value)
        syncResult.value = result
        await retryFetchContacts()
      } catch (e) {
        syncResult.value = { status: 'error', error: e.message }
      } finally {
        syncing.value = false
      }
    }

    const openContactDetail = (contact) => {
      selectedContact.value = contact
      showDetailModal.value = true
    }

    const openLinkModal = (contact) => {
      linkContact.value = contact
      supplierSearch.value = ''
      selectedSupplierForLink.value = null
      selectedLocationForLink.value = null
      showLinkModal.value = true
    }

    const closeLinkModal = () => {
      showLinkModal.value = false
      linkContact.value = null
      selectedSupplierForLink.value = null
      selectedLocationForLink.value = null
    }

    const selectSupplierForLink = (supplier) => {
      selectedSupplierForLink.value = supplier
      selectedLocationForLink.value = null
    }

    // Can link: supplier selected + (no locations OR location selected) + not already linked
    const canLink = computed(() => {
      if (!selectedSupplierForLink.value) return false
      const supplier = selectedSupplierForLink.value
      const hasLocations = supplier.locations && supplier.locations.length > 0
      if (hasLocations) {
        if (!selectedLocationForLink.value) return false
        return !selectedLocationForLink.value.odooId
      }
      return !supplier.odooId
    })

    const handleLink = async () => {
      if (!linkContact.value || !selectedSupplierForLink.value) return
      linkingInProgress.value = true
      try {
        const locationId = selectedLocationForLink.value?.locationid || null
        await linkSupplier(linkContact.value.odooId, selectedSupplierForLink.value.id, locationId)
        const target = selectedLocationForLink.value
          ? (selectedLocationForLink.value.locationname || 'Location ' + selectedLocationForLink.value.locationid)
          : selectedSupplierForLink.value.suppliername
        showToast('success', `Linked "${linkContact.value.name}" to "${target}"`)
        closeLinkModal()
        await retryFetchContacts()
      } catch (e) {
        showToast('error', 'Failed to link supplier. Please try again.')
      } finally {
        linkingInProgress.value = false
      }
    }

    const openCreateModal = (contact) => {
      createContact.value = contact
      newSupplierName.value = contact.name
      newSupplierSiteId.value = store.state.currentSite || ''
      showCreateModal.value = true
    }

    const closeCreateModal = () => {
      showCreateModal.value = false
      createContact.value = null
      newSupplierName.value = ''
      newSupplierSiteId.value = ''
    }

    const handleCreate = async () => {
      if (!createContact.value || !newSupplierName.value || !newSupplierSiteId.value) return
      creatingInProgress.value = true
      try {
        const result = await createSupplier(createContact.value.odooId, newSupplierName.value, newSupplierSiteId.value)
        showToast('success', `Supplier "${result.supplierName}" created`)
        closeCreateModal()
        await retryFetchContacts()
      } catch (e) {
        showToast('error', 'Failed to create supplier. Please try again.')
      } finally {
        creatingInProgress.value = false
      }
    }

    const openUnlinkConfirm = (contact) => {
      unlinkContact.value = contact
      showUnlinkModal.value = true
    }

    const handleUnlink = async () => {
      if (!unlinkContact.value) return
      unlinkingInProgress.value = true
      try {
        const locationId = unlinkContact.value.linkedLocationId || null
        await unlinkSupplier(unlinkContact.value.odooId, unlinkContact.value.linkedSupplierId, locationId)
        showToast('success', `Unlinked "${unlinkContact.value.name}"`)
        showUnlinkModal.value = false
        unlinkContact.value = null
        await retryFetchContacts()
      } catch (e) {
        showToast('error', 'Failed to unlink supplier. Please try again.')
      } finally {
        unlinkingInProgress.value = false
      }
    }

    // Init
    onMounted(async () => {
      checkHealth()
      fetchCompanies()
      retryFetchContacts()
      loadSuppliers()
    })

    return {
      loading, error, contacts, retryFetchContacts,
      activeTab, tablePage, selectedCompany, companies, syncing, syncResult,
      tableColumns, filteredContacts, displayedContacts, unmatchedCount, linkedCount,
      connectionClass, dotClass, connectionLabel,
      odooConnected, checkingHealth, recheckConnection,
      showDetailModal, selectedContact,
      showLinkModal, linkContact, supplierSearch, selectedSupplierForLink,
      selectedLocationForLink, selectSupplierForLink, canLink,
      filteredSuppliers, suppliersLoaded, isSupplierFullyLinked, linkingInProgress,
      showCreateModal, createContact, newSupplierName, newSupplierSiteId,
      creatingInProgress, userSites,
      showUnlinkModal, unlinkContact, unlinkingInProgress,
      formatDate, handleSync, openContactDetail,
      openLinkModal, closeLinkModal, handleLink,
      openCreateModal, closeCreateModal, handleCreate,
      openUnlinkConfirm, handleUnlink
    }
  }
}
</script>

<style scoped>
.odoo-page > * {
  animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.odoo-page > *:nth-child(1) { animation-delay: 0s; }
.odoo-page > *:nth-child(2) { animation-delay: 0.04s; }
.odoo-page > *:nth-child(3) { animation-delay: 0.08s; }
.odoo-page > *:nth-child(4) { animation-delay: 0.12s; }

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .odoo-page > * { animation: none; }
}
</style>
